import React from 'react';
import { isMobile, isDesktop } from 'react-device-detect';
import {
  compose,
  withHandlers,
  withState,
  withStateHandlers,
} from 'recompose';
import camelize from 'camelize';

import { HTMLTrans, withTranslation } from '../Common/Translations';
import { WithMethods } from '../Common/Providers/CustomerVoucherApi'
import ActivateButton from '../Account/Voucher/ActivateButton'
import { withDataLayerActivateVoucher } from '../Common/GTM/ECommerceTracking/withDataLayerActivateVoucher'

const VoucherActivationPageContainer = (props) => {
  const {
    onAuthenticated = () => null,
    handleOnClickCallToAction = () => null,
    disable = false,
    inputCode,
    onChange,
    errorMessage,
    t
  } = props

  return (
    <div className='ml-3 mr-3'>
      <div className="bg-yellow container-fluid a-center">
        <div className="row">
          <div className="col-lg-12 p-0 align-self-center">
            <div className="d-none d-lg-block">
              <div className="h-300">
                <img className="img-fluid" alt src={t("voucher_activation_page.voucher_activation_banner_web", "https://media2.pbwwcdn.net/pages/landing/2019/activation/activation_banner_01.jpg")} />
              </div>
            </div>
            <div className="d-lg-none">
              <img className="img-fluid" alt src={t("voucher_activation_page.voucher_activation_banner_mobile", "https://media2.pbwwcdn.net/pages/landing/2019/activation/activation_banner_mobile.jpg")} />
            </div>
          </div>
        </div>
      </div>
      <div className='container container-fluid no-gutter mt-5'>
        <div className='row justify-content-md-center'>
          <div className='col-lg-6 col-12'>
            <div className='text-center'>
              <h6>
                <span className='ProximaNova-Bold uppercase mt-5'>
                  <p className='medium'>
                    <HTMLTrans id="voucher_activation_page.voucher_activation_title">
                      If you have purchased a voucher code from our External Platforms (e.g. Groupon), 
                      fill in your details below to get your purchased voucher code activated
                    </HTMLTrans>
                  </p>
                </span>
              </h6>
            </div>
            <div>
              <div className='mt-5'>
                <p>
                  <span className='ProximaNova-Bold'>
                    <HTMLTrans id="voucher_activation_page.voucher_activation_button_description">
                        Voucher Code
                    </HTMLTrans>
                  </span>
                  <div className='mt-1'>
                    <input
                      type='text'
                      className='form-control border border-secondary mb-1'              
                      placeholder={t('Enter Voucher Code here')}
                      onChange={onChange}
                      value={inputCode}
                    />
                    <small className="ProximaNova-Regular mb-3">
                      <HTMLTrans id="voucher_activation_page.voucher_activation_button_sub_description">
                        *Voucher code is case-sensitive.
                      </HTMLTrans>
                    </small>
                  </div>
                </p>
                <div className='text-danger'>
                  <p>
                    {
                      errorMessage
                    }
                  </p>
                </div>
              </div>
              <div className='text-center'>
                {
                  <ActivateButton
                    onAuthenticated={onAuthenticated}
                    onClick={handleOnClickCallToAction}
                    disable={disable}
                    inputCode={inputCode}
                    />
                }
              </div>
              <div className='mt-5'>
                <span className='ProximaNova'>
                  <small>
                    <HTMLTrans id="voucher_activation_page.voucher_activation_tnc">
                      By clicking Activate Now, you agree to Photobook's <a target="_blank" href="/return-policy/terms-of-use">Terms of Use</a> and <a target="_blank" href="/return-policy/privacy-policy">Privacy Policy</a>. In addition, you will be subscribed to receive exclusive email offers.
                    </HTMLTrans>
                  </small>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='container container-fluid mb-5'>
        <div className='row justify-content-center no-gutters'>
          <div className='col-lg-8 my-5 bg-light p-5'>
            <h5 className="ProximaNova-Bold uppercase mb-3">
              <HTMLTrans id="voucher_activation_page.voucher_activation_how_to_activate">
                How to activate
              </HTMLTrans>
            </h5>
            <h6 className="ProximaNova-Regular mb-3">
              <HTMLTrans id="voucher_activation_page.voucher_activation_how_to_activate_1">
                Step 1: Fill in your voucher code.
              </HTMLTrans>
            </h6>
            <h6 className="ProximaNova-Regular mb-3">
              <HTMLTrans id="voucher_activation_page.voucher_activation_how_to_activate_2">
                Step 2: Once your voucher code is activated, start creating your project.
              </HTMLTrans>
            </h6>
            <h6 className="ProximaNova-Regular mb-3">
              <HTMLTrans id="voucher_activation_page.voucher_activation_how_to_activate_3">
                Step 3: Choose the product which matches your voucher, begin personalising.
              </HTMLTrans>
            </h6>
            <h6 className="ProximaNova-Regular mb-3">
              <HTMLTrans id="voucher_activation_page.voucher_activation_how_to_activate_4">
                Step 4: Checkout with your voucher code and we'll deliver your project within 7 business days*.
              </HTMLTrans>
            </h6>
          </div>
        </div>
      </div>
    </div>
  )
};

const isRedirectToAppPushPage = (platforms) => {
  if (platforms.length == 1 && platforms[0] == 'mobile') {
    return true
  }

  return false
}

const withActivationFormState = compose(
  withState('isActivated', 'setActivated', false),
  withState('errorMessage', 'setErrorMessage', ''),
)

const withOnChange = compose(
  withStateHandlers({
    inputCode: ''
  }, {
    onChange: () => ({ target: { value } }) => ({
      inputCode: value
    }),
    clearInputCode: () => () => ({
      inputCode: ''
    }),
  }),
);

const enhance = compose(
  withActivationFormState,
  withOnChange,
  WithMethods,
  withTranslation('voucher_activation_page'),
  withDataLayerActivateVoucher,
  withHandlers({
    handleOnClickCallToAction: (props) => async () => {
      const { 
        inputCode, 
        clearInputCode, 
        activateVoucher, 
        setActivated,
        setErrorMessage,
        t,
        tRoute,
        dataLayerActivateVoucher
      } = props

      try {
        const { success, data } = await activateVoucher(inputCode);

        if (success) {
          const { 
            claimVoucher: { 
              promotionPlatforms 
            }
          } = camelize(data)

          setActivated(true)
          clearInputCode();
          const redirectToAppPushPage = isRedirectToAppPushPage(promotionPlatforms)
          
          if (redirectToAppPushPage) {
            window.location.href = tRoute('/appvoucher')
          } else {
            window.location.href = tRoute('/account/dashboard/voucher')
          }

          dataLayerActivateVoucher({
            platform: isMobile ? 'mobile' : 'web'
          })

          toastr.success(
              t("voucher_activation_page.voucher_success_activated", "Voucher is successfully activated!"), '',
              { positionClass: 'toast-bottom-full-width' },
            );
        }
      } catch (error) {
        const { responseJSON } = error
        setActivated(false)
        clearInputCode();
        setErrorMessage(responseJSON.errors)
      }
    },
  }),
  withHandlers({
    onAuthenticated: ({ handleOnClickCallToAction }) => () => {
      handleOnClickCallToAction();
    },
  }),
)

export default enhance(VoucherActivationPageContainer);


// WEBPACK FOOTER //
// ./src/StaticPage/VoucherActivation.js